import axios from 'axios';
import { AppConfig, JWT } from '../config';
import { version as appVersion } from '../../package.json';

export const FEEDBACK_HUB_SUBMIT_URL = 'https://tool.rayplatform.com/api/feedback/submit';
// 허브 공개 제출키(fbp_)는 브라우저 노출을 전제로 발급된 제출 전용 키(조회 불가).
const FEEDBACK_HUB_PUBLIC_KEY = 'fbp_d0eb6d0f06d1f0d7094760f2ea64fed1e8a0886592fbfa6396a1c43dd0e95cdb';
// 허브 meta 전체 20,000자 제한 대비: question 은 화면에서 1000자 제한, answer 만 자른다.
const HUB_MAX_ANSWER_LENGTH = 15000;

// FEAT-061: RAYGuard AI 질의. 절대 throw 하지 않고 { ok, ... } 를 반환한다.
export const askRayGuardAi = async (question) => {
	try {
		const ret = await axios.post(AppConfig.API_SERVER_PATH + '/ai/ask', { type: 'ask', data: { question } }, JWT());
		if (ret?.data?.status === 'success') {
			// 의도적 폴백: BE 가 RResult 래핑({data:{answer}}) 없이 최상위 {answer, logId} 로 줄 때도 허용
			const answer = ret?.data?.data?.answer ?? ret?.data?.answer;
			const logId = ret?.data?.data?.logId ?? ret?.data?.logId;
			if (typeof answer === 'string' && answer.trim() !== '') {
				return { ok: true, answer, logId };
			}
			console.warn('[AiSearchAction.askRayGuardAi] failed', { handler: 'askRayGuardAi', reason: 'empty answer' });
			return { ok: false, code: 'error', message: 'empty answer' };
		}
		console.warn('[AiSearchAction.askRayGuardAi] failed', { handler: 'askRayGuardAi', reason: ret?.data?.message || 'unsuccessful response' });
		return { ok: false, code: 'error', message: ret?.data?.message };
	} catch (error) {
		const httpStatus = error?.response?.status;
		const code = httpStatus === 403 ? 'forbidden' : httpStatus === 429 ? 'limit' : 'error';
		console.warn('[AiSearchAction.askRayGuardAi] failed', { handler: 'askRayGuardAi', reason: `${code} (${httpStatus ?? error?.message})` });
		return { ok: false, code, message: error?.response?.data?.message };
	}
};

// FEAT-061: 답변 피드백(좋아요/아쉬움 + 의견). rating = 'up' | 'down'. throw 하지 않는다.
export const sendRayGuardAiFeedback = async (logId, rating, comment) => {
	try {
		const ret = await axios.post(AppConfig.API_SERVER_PATH + '/ai/ask', { type: 'feedback', data: { logId, rating, comment } }, JWT());
		if (ret?.data?.status === 'success') {
			return { ok: true };
		}
		console.warn('[AiSearchAction.sendRayGuardAiFeedback] failed', { handler: 'sendRayGuardAiFeedback', reason: ret?.data?.message || 'unsuccessful response' });
		return { ok: false, message: ret?.data?.message };
	} catch (error) {
		console.warn('[AiSearchAction.sendRayGuardAiFeedback] failed', { handler: 'sendRayGuardAiFeedback', reason: error?.response?.status ?? error?.message });
		return { ok: false, message: error?.response?.data?.message };
	}
};

// FEAT-061: 같은 피드백을 피드백 허브로도 전송. throw/reject 하지 않는다.
export const sendRayGuardAiFeedbackToHub = async ({ logId, rating, comment, question, answer, user } = {}) => {
	if (rating !== 'up' && rating !== 'down') {
		console.warn('[AiSearchAction.sendRayGuardAiFeedbackToHub] skipped', { handler: 'sendRayGuardAiFeedbackToHub', logId, reason: 'invalid rating' });
		return { ok: false };
	}
	let status;
	try {
		const isUp = rating === 'up';
		const trimmed = typeof comment === 'string' ? comment.trim() : '';
		const body = {
			kind: isUp ? 'praise' : 'other',
			rating: isUp ? 5 : 1,
			content: trimmed || (isUp ? '👍' : '👎'),
			product_version: appVersion,
			meta: {
				source: 'rayguard-ai',
				logId,
				question,
				answer: typeof answer === 'string' ? answer.slice(0, HUB_MAX_ANSWER_LENGTH) : answer,
				companyId: user?.companyId,
			},
		};
		const adminid = user?.adminid;
		if (typeof adminid === 'string' && adminid.trim() !== '') body.contact = adminid;

		// JWT() 금지: Authorization 등 다른 헤더가 붙으면 허브 CORS 사전 확인(허용 헤더 = Content-Type, X-Feedback-Key)에서 브라우저가 차단한다.
		const ret = await axios.post(FEEDBACK_HUB_SUBMIT_URL, body, {
			headers: { 'X-Feedback-Key': FEEDBACK_HUB_PUBLIC_KEY, 'Content-Type': 'application/json' },
		});
		status = ret?.status;
		if (ret?.data?.ok === true) {
			const warnings = ret?.data?.warnings;
			if (Array.isArray(warnings) && warnings.length > 0) {
				console.warn('[AiSearchAction.sendRayGuardAiFeedbackToHub] hub ignored some values', { handler: 'sendRayGuardAiFeedbackToHub', logId, warnings });
			}
			return { ok: true, id: ret?.data?.id };
		}
		console.warn('[AiSearchAction.sendRayGuardAiFeedbackToHub] failed', { handler: 'sendRayGuardAiFeedbackToHub', logId, reason: status ?? 'unsuccessful response' });
		return { ok: false, status };
	} catch (error) {
		status = error?.response?.status;
		console.warn('[AiSearchAction.sendRayGuardAiFeedbackToHub] failed', { handler: 'sendRayGuardAiFeedbackToHub', logId, reason: status ?? error?.message });
		return { ok: false, status };
	}
};
